/**
 * Write Adapters for the Data Grid
 *
 * Some list columns are hydrated from a different table than the one they
 * appear to live on. For example, `Item.standard_rate` is displayed in the
 * item list but the value is actually sourced from `tabItem Price` by
 * `zaviago_backend.api.item_list.list_items_with_variants` (STEP 6b).
 *
 * Writing to `Item.standard_rate` via `frappe.client.set_value` succeeds but
 * lands in the wrong column — on refetch, the list reads Item Price again and
 * the edit silently disappears.
 *
 * Write adapters let fields route through a matching server method so writes
 * hit the same source the read path uses. Without an adapter, the grid falls
 * back to the default `frappe.client.set_value` path.
 *
 * Registry key format: `${doctype}.${fieldname}`
 */

import { frappeMethod } from '@/lib/erp/utils';
import type { QueryClient } from '@tanstack/react-query';

export interface WriteAdapterContext {
  /** Document name (row identifier) */
  name: string;
  value: unknown;
  oldValue: unknown;
}

export interface WriteAdapterResult {
  /** Server-returned modified timestamp for conflict detection (optional) */
  modified?: string;
  /** Extra query keys to invalidate in addition to the list cache */
  invalidateQueryKeys?: unknown[][];
}

export type WriteAdapter = (ctx: WriteAdapterContext) => Promise<WriteAdapterResult>;

/**
 * Registry of field-specific write adapters.
 *
 * Add an entry here when a list column is hydrated from a different table
 * than `tab{DocType}` in a whitelisted list method.
 */
const WRITE_ADAPTERS: Record<string, WriteAdapter> = {
  /**
   * Item.standard_rate — list reads from `tabItem Price` (active price list)
   * via `list_items_with_variants` STEP 6b. Route inline edits through the
   * same endpoint the detail page uses so the write lands in Item Price.
   */
  'Item.standard_rate': async ({ name, value }) => {
    const rate = Number(value);
    if (!Number.isFinite(rate)) {
      throw new Error('Standard rate must be a number');
    }
    await frappeMethod(
      'zaviago_backend.api.pricing_summary.update_item_base_price',
      { item_code: name, rate },
    );
    return {
      // Item Price write — also invalidate pricing-summary so the detail
      // panel's price badge stays consistent.
      invalidateQueryKeys: [
        ['pricing-summary', name],
        ['item', name],
      ],
    };
  },
};

/**
 * Look up a write adapter for a (doctype, fieldname) pair.
 * Returns null if no adapter is registered — caller should fall back to
 * `frappe.client.set_value`.
 */
export function getWriteAdapter(
  doctype: string,
  fieldname: string,
): WriteAdapter | null {
  return WRITE_ADAPTERS[`${doctype}.${fieldname}`] ?? null;
}

/**
 * Invalidate extra query keys returned by a write adapter.
 * Called after a successful adapter save.
 */
export function invalidateAdapterKeys(
  queryClient: QueryClient,
  result: WriteAdapterResult,
): void {
  if (!result.invalidateQueryKeys) return;
  for (const queryKey of result.invalidateQueryKeys) {
    queryClient.invalidateQueries({ queryKey });
  }
}
